import AppContext from 'App/AppContext';
import posthogJs from 'posthog-js';
import { PostHogProvider } from 'posthog-js/react';
import type { ReactNode } from 'react';
import { useContext, useEffect } from 'react';
import logger from 'services/Logging/logService';

const posthogKey = 'phc_qN9EiK4GDZpUmnmtXbyncydRbGh2aRZC3oCfGPAVBt6p';
const posthogHost = 'https://eu.i.posthog.com';
let posthogInitFailed = false;

export function PostHogAnalyticsProvider({ children }: { children: ReactNode }) {
  const appContext = useContext(AppContext);

  const tenantId = appContext.user.login.tenantId || appContext.user.tenant.id;
  const tenantName = appContext.user.tenant.name;
  const userId = appContext.user.login.userId || appContext.user.id;
  const userName = appContext.user.name;
  const userEmail = appContext.user.email;

  // Only enable PostHog for tenants that are in their trial period.
  const isInTrial = appContext.user.login.isInTrial();

  useEffect(() => {
    if (!posthogKey || posthogInitFailed || !isInTrial || !posthogJs.__loaded || !tenantId || !userId) {
      return;
    }

    try {
      posthogJs.register({
        tenantId: tenantId,
        tenantName: tenantName,
        userId: userId,
        userName: userName,
        userEmail: userEmail,
      });

      posthogJs.identify(userId, {
        tenantId: tenantId,
        tenantName: tenantName,
        userId: userId,
        userName: userName,
        userEmail: userEmail,
      });

      posthogJs.group('tenant', tenantId, {
        tenantId: tenantId,
        tenantName: tenantName,
      });
    } catch (err) {
      // Ignore errors, as PostHog is not critical to the application.
      posthogInitFailed = true;
      logger.debug('PostHogAnalyticsProvider: Failed to register user or group with PostHog.', err);

      return;
    }
  }, [isInTrial, tenantId, tenantName, userId, userName, userEmail]);

  //
  // Main render
  //
  if (!posthogKey || posthogInitFailed || !isInTrial) {
    return <>{children}</>;
  }

  try {
    if (!posthogJs.__loaded) {
      posthogJs.init(posthogKey, {
        api_host: posthogHost,

        capture_pageview: true,
        capture_pageleave: true,

        session_recording: {
          maskAllInputs: true,
          maskTextSelector: null,
          maskInputOptions: {
            password: true,
            email: true,
          },
        },
      });
    }
  } catch (err) {
    // Ignore errors, as PostHog is not critical to the application.
    posthogInitFailed = true;
    logger.debug('PostHogAnalyticsProvider: Failed to init PostHog.', err);

    return <>{children}</>;
  }

  return <PostHogProvider client={posthogJs}>{children}</PostHogProvider>;
}
